import styled from 'styled-components';
import { Bio } from '../../data/constants';
import Typewriter from 'typewriter-effect';
import { motion } from 'framer-motion';
import { GitHub, LinkedIn } from '@mui/icons-material';
import { SiUpwork } from 'react-icons/si';
import { headContainerAnimation, headTextAnimation } from '../../utils/motion';
import StarCanvas from '../canvas/Stars';

const HeroContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  min-height: calc(100vh - 80px);
  padding: 72px 24px 120px;
  z-index: 1;
  overflow: hidden;
  clip-path: polygon(0 0, 100% 0, 100% 100%, 70% 95%, 0 100%);

  @media screen and (max-width: 960px) {
    padding: 56px 20px 96px;
    min-height: auto;
  }

  @media screen and (max-width: 640px) {
    padding: 40px 16px 88px;
  }
`;

const HeroBg = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const Glow = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.45;
  pointer-events: none;

  &.one {
    width: 420px;
    height: 420px;
    background: rgba(133, 76, 230, 0.35);
    top: -80px;
    left: 12%;
  }

  &.two {
    width: 360px;
    height: 360px;
    background: rgba(201, 32, 184, 0.22);
    right: 8%;
    bottom: 40px;
  }
`;

const HeroInnerContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
  max-width: 860px;
  z-index: 2;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  margin-bottom: 22px;
  border-radius: 999px;
  border: 1px solid rgba(133, 76, 230, 0.45);
  background: rgba(133, 76, 230, 0.12);
  color: ${({ theme }) => theme.primary};
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.18);
`;

const Title = styled.h1`
  margin: 0;
  font-size: 72px;
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: -0.03em;
  color: ${({ theme }) => theme.text_primary};

  @media screen and (max-width: 960px) {
    font-size: 56px;
  }

  @media screen and (max-width: 640px) {
    font-size: 40px;
  }
`;

const TextLoop = styled.div`
  margin-top: 16px;
  font-size: 28px;
  font-weight: 600;
  min-height: 42px;
  color: ${({ theme }) => theme.primary};

  @media screen and (max-width: 640px) {
    font-size: 20px;
    min-height: 32px;
  }
`;

const SubTitle = styled.p`
  margin: 28px 0 0;
  max-width: 720px;
  font-size: 18px;
  line-height: 1.7;
  font-weight: 400;
  color: ${({ theme }) => theme.text_secondary};

  @media screen and (max-width: 640px) {
    font-size: 16px;
    line-height: 1.65;
  }
`;

const Highlights = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-top: 28px;
`;

const Chip = styled.span`
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(17, 25, 40, 0.55);
  color: ${({ theme }) => theme.text_primary};
  font-size: 13px;
  font-weight: 500;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 36px;
  width: 100%;
`;

const ResumeButton = styled.a`
  text-decoration: none;
  min-width: 200px;
  text-align: center;
  padding: 14px 28px;
  color: #fff;
  background: linear-gradient(225deg, hsla(271, 100%, 50%, 1) 0%, hsla(294, 100%, 50%, 1) 100%);
  border-radius: 50px;
  font-size: 16px;
  font-weight: 600;
  transition: transform 0.3s ease, filter 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    filter: brightness(1.08);
  }
`;

const SecondaryButton = styled.a`
  text-decoration: none;
  min-width: 160px;
  text-align: center;
  padding: 14px 28px;
  color: ${({ theme }) => theme.primary};
  border: 1.5px solid ${({ theme }) => theme.primary};
  border-radius: 50px;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.3s ease-in-out;

  &:hover {
    background: ${({ theme }) => theme.primary};
    color: ${({ theme }) => theme.text_primary};
  }
`;

const Socials = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 28px;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: ${({ theme }) => theme.text_primary};
  transition: all 0.25s ease;

  &:hover {
    border-color: ${({ theme }) => theme.primary};
    color: ${({ theme }) => theme.primary};
    transform: translateY(-2px);
  }
`;

const highlights = ['React.js', 'Node.js', 'TypeScript', 'GCP', 'AI Integrations'];

const Hero = () => {
  return (
    <div id="About">
      <HeroContainer>
        <HeroBg>
          <Glow className="one" />
          <Glow className="two" />
          <StarCanvas />
        </HeroBg>
        <motion.div {...headContainerAnimation}>
          <HeroInnerContainer>
            <motion.div {...headTextAnimation}>
              <Eyebrow>
                <Dot />
                Full Stack Software Engineer at Sadiq.ai
              </Eyebrow>
              <Title>{Bio.name}</Title>
              <TextLoop>
                <Typewriter
                  options={{ strings: Bio.roles, autoStart: true, loop: true }}
                />
              </TextLoop>
            </motion.div>
            <SubTitle>{Bio.description}</SubTitle>
            <Highlights>
              {highlights.map((item) => (
                <Chip key={item}>{item}</Chip>
              ))}
            </Highlights>
            <ButtonRow>
              <ResumeButton href={Bio.resume} target="_blank" rel="noreferrer">
                Download Resume
              </ResumeButton>
              <SecondaryButton href="#Contact">Contact Me</SecondaryButton>
            </ButtonRow>
            <Socials>
              <SocialLink href={Bio.github} target="_blank" rel="noreferrer" aria-label="GitHub">
                <GitHub />
              </SocialLink>
              <SocialLink href={Bio.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn">
                <LinkedIn />
              </SocialLink>
              <SocialLink href={Bio.upwork} target="_blank" rel="noreferrer" aria-label="Upwork">
                <SiUpwork size={20} />
              </SocialLink>
            </Socials>
          </HeroInnerContainer>
        </motion.div>
      </HeroContainer>
    </div>
  );
};

export default Hero;
